import { useEffect, useMemo, useState, type KeyboardEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert, Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, Grid, IconButton,
  InputAdornment, MenuItem, Stack, Switch, TextField, Tooltip, Typography,
} from '@mui/material';
import { Add, AutoFixHigh, DeleteOutline, EditOutlined, Refresh, WarningAmber } from '@mui/icons-material';
import { GridColDef } from '@mui/x-data-grid';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import PageShell from '../../components/layout/PageShell';
import PageHeader from '../../components/layout/PageHeader';
import DataTableCard from '../../components/ui/DataTableCard';
import EnhancedDataGrid from '../../components/ui/EnhancedDataGrid';
import ConfirmDialog from '../../components/ui/ConfirmDialog';
import {
  getMasters, createMaster, updateMaster, deleteMaster, getPromoCouponStats, type PromoCouponStat,
} from '../../services/platform/masterService';
import { getApiErrorMessage } from '../../services/userService';
import { isNetworkError } from '../../utils/networkError';
import { formatMoney, getCurrencySymbol } from '../../utils/currency';
import { repetitionError } from '../../constants/homeBanners';
import {
  PROMO_COUPON_TYPE,
  PROMO_COUPON_MODULES,
  PROMO_COUPON_AUDIENCES,
  PROMO_COUPON_LIMITS,
  PROMO_COUPON_CODE_PATTERN,
  DEFAULT_NEW_USER_DAYS,
  couponModuleLabel,
  audienceLabel,
  suggestCouponCode,
} from '../../constants/promoCoupons';

const DISPLAY_DATE_FORMAT = 'MM/DD/YYYY';

interface CouponMeta {
  module?: string;
  discountType?: string;
  discountValue?: number | string;
  minOrderAmount?: number | string;
  maxDiscount?: number | string;
  usageLimit?: number | string;
  maxUsesPerUser?: number | string;
  audience?: string;
  newUserDays?: number | string;
  validFrom?: string;
  validTo?: string;
  description?: string;
  sortOrder?: number;
}

interface CouponRow {
  _id: string;
  tenantId?: string;
  code: string;
  name: string;
  sortOrder?: number;
  isActive: boolean;
  metadata?: CouponMeta;
}

type ValidityState = 'open' | 'scheduled' | 'live' | 'expired';

interface CouponForm {
  name: string;
  code: string;
  description: string;
  module: string;
  discountType: string;
  discountValue: string;
  maxDiscount: string;
  minOrderAmount: string;
  usageLimit: string;
  maxUsesPerUser: string;
  audience: string;
  newUserDays: string;
  validFrom: string;
  validTo: string;
  sortOrder: string;
}

type FormErrors = Partial<Record<keyof CouponForm, string>>;

const FIELD_LABELS: Record<keyof CouponForm, string> = {
  name: 'Coupon name',
  code: 'Code',
  description: 'Description',
  module: 'Service',
  discountType: 'Discount Type',
  discountValue: 'Discount Value',
  maxDiscount: 'Max Discount',
  minOrderAmount: 'Minimum Order',
  usageLimit: 'Total Usage Limit',
  maxUsesPerUser: 'Max Uses Per User',
  audience: 'Eligible Customers',
  newUserDays: 'New customer window',
  validFrom: 'Valid From',
  validTo: 'Valid To',
  sortOrder: 'Display Order',
};

const EMPTY_FORM: CouponForm = {
  name: '', code: '', description: '', module: '', discountType: '', discountValue: '', maxDiscount: '',
  minOrderAmount: '0', usageLimit: '', maxUsesPerUser: '1', audience: 'all', newUserDays: String(DEFAULT_NEW_USER_DAYS),
  validFrom: '', validTo: '', sortOrder: '',
};

const VALIDITY_CHIP: Record<ValidityState, { label: string; color: 'default' | 'success' | 'info' | 'error' | 'warning' }> = {
  open: { label: 'No end date', color: 'warning' },
  live: { label: 'Running', color: 'success' },
  scheduled: { label: 'Scheduled', color: 'info' },
  expired: { label: 'Expired', color: 'error' },
};

function todayIso() {
  return dayjs().format('YYYY-MM-DD');
}

function formatDate(value?: string) {
  if (!value) return '';
  const d = dayjs(value);
  return d.isValid() ? d.format(DISPLAY_DATE_FORMAT) : value;
}

function blank(value: unknown) {
  return value === undefined || value === null || String(value).trim() === '';
}

function toNumber(value: unknown): number | null {
  return blank(value) ? null : Number(value);
}

function couponOrder(row: CouponRow) {
  const top = Number(row.sortOrder);
  if (Number.isFinite(top) && top >= 1) return top;
  const meta = Number(row.metadata?.sortOrder);
  return Number.isFinite(meta) ? meta : 0;
}

function validityState(meta?: CouponMeta): ValidityState {
  const from = meta?.validFrom || '';
  const to = meta?.validTo || '';
  const today = todayIso();
  if (to && to < today) return 'expired';
  if (from && from > today) return 'scheduled';
  if (to) return 'live';
  return 'open';
}

function nextFreeOrder(rows: CouponRow[], moduleKey: string, excludeId?: string) {
  const used = new Set(rows.filter((r) => r._id !== excludeId && r.metadata?.module === moduleKey).map(couponOrder));
  let n = 1;
  while (used.has(n)) n += 1;
  return n;
}

/** Problems in a saved coupon that make it unsafe to switch on (e.g. legacy or seeded data). */
function configIssues(row: CouponRow): string[] {
  const m = row.metadata || {};
  const issues: string[] = [];
  if (!PROMO_COUPON_MODULES.some((x) => x.value === m.module)) issues.push('Service is not set');
  const value = toNumber(m.discountValue);
  if (m.discountType === 'percentage') {
    if (value == null || !(value > 0 && value <= PROMO_COUPON_LIMITS.maxPercent)) issues.push('Percentage must be between 0 and 100');
  } else if (m.discountType === 'fixed') {
    if (value == null || !(value > 0)) issues.push('Fixed discount must be greater than 0');
    if (!blank(m.maxDiscount)) issues.push('Max Discount is set on a fixed-amount coupon');
  } else {
    issues.push('Discount Type is not set');
  }
  const minOrder = toNumber(m.minOrderAmount);
  if (minOrder != null && !(minOrder >= 0)) issues.push('Minimum Order is negative');
  const total = toNumber(m.usageLimit);
  const perUser = toNumber(m.maxUsesPerUser);
  if (total != null && !(Number.isInteger(total) && total >= 1)) issues.push('Total Usage Limit is not a positive whole number');
  if (perUser != null && !(Number.isInteger(perUser) && perUser >= 1)) issues.push('Max Uses Per User is not a positive whole number');
  if (total != null && perUser != null && perUser > total) issues.push('Max Uses Per User exceeds Total Usage Limit');
  if (!m.validTo) issues.push('Valid To (expiry) is missing');
  else if (m.validFrom && m.validTo < m.validFrom) issues.push('Valid To is before Valid From');
  return issues;
}

function rowToForm(row: CouponRow): CouponForm {
  const m = row.metadata || {};
  return {
    name: row.name || '',
    code: row.code || '',
    description: String(m.description || ''),
    module: m.module || '',
    discountType: m.discountType || '',
    discountValue: blank(m.discountValue) ? '' : String(m.discountValue),
    maxDiscount: blank(m.maxDiscount) ? '' : String(m.maxDiscount),
    minOrderAmount: blank(m.minOrderAmount) ? '0' : String(m.minOrderAmount),
    usageLimit: blank(m.usageLimit) || Number(m.usageLimit) === 0 ? '' : String(m.usageLimit),
    maxUsesPerUser: blank(m.maxUsesPerUser) ? '1' : String(m.maxUsesPerUser),
    audience: m.audience === 'new_users' ? 'new_users' : 'all',
    newUserDays: blank(m.newUserDays) ? String(DEFAULT_NEW_USER_DAYS) : String(m.newUserDays),
    validFrom: m.validFrom || '',
    validTo: m.validTo || '',
    sortOrder: String(couponOrder(row) || ''),
  };
}

function validateForm(form: CouponForm, rows: CouponRow[], editingId: string | undefined, wasActive: boolean): FormErrors {
  const e: FormErrors = {};
  const name = form.name.trim();
  if (!name) e.name = 'Coupon name is required';
  else if (name.length > PROMO_COUPON_LIMITS.name) e.name = `Max ${PROMO_COUPON_LIMITS.name} characters`;
  else {
    const dup = rows.find((r) => r._id !== editingId && r.name.trim().toLowerCase() === name.toLowerCase());
    if (dup) e.name = `Already used by ${dup.code} — use a distinct name`;
  }

  if (!editingId) {
    const code = form.code.trim();
    if (!code) e.code = 'Code is required — use Auto-generate';
    else if (code.length < PROMO_COUPON_LIMITS.codeMin || code.length > PROMO_COUPON_LIMITS.codeMax) {
      e.code = `${PROMO_COUPON_LIMITS.codeMin}–${PROMO_COUPON_LIMITS.codeMax} characters`;
    } else if (!PROMO_COUPON_CODE_PATTERN.test(code)) e.code = 'Letters A–Z and digits 0–9 only';
    else if (rows.some((r) => r.code.trim().toUpperCase() === code)) e.code = `${code} already exists — codes must be unique`;
  }

  const description = form.description.trim();
  if (description.length > PROMO_COUPON_LIMITS.description) e.description = `Max ${PROMO_COUPON_LIMITS.description} characters`;
  else e.description = repetitionError('Description', description);

  if (!form.module) e.module = 'Service is required';
  if (!form.discountType) e.discountType = 'Discount Type is required';

  const value = toNumber(form.discountValue);
  if (form.discountType) {
    if (value == null) e.discountValue = 'Discount Value is required';
    else if (!Number.isFinite(value)) e.discountValue = 'Enter a number';
    else if (form.discountType === 'percentage' && !(value > 0 && value <= PROMO_COUPON_LIMITS.maxPercent)) {
      e.discountValue = 'Percentage must be greater than 0 and at most 100';
    } else if (form.discountType === 'fixed' && !(value > 0 && value <= PROMO_COUPON_LIMITS.maxAmount)) {
      e.discountValue = `Amount must be greater than 0 and at most ${PROMO_COUPON_LIMITS.maxAmount}`;
    }
  }

  const minOrder = toNumber(form.minOrderAmount);
  if (minOrder != null && (!Number.isFinite(minOrder) || minOrder < 0)) e.minOrderAmount = 'Must be 0 or a positive amount';
  else if (minOrder != null && Math.round(minOrder * 100) !== minOrder * 100) e.minOrderAmount = 'At most 2 decimal places';
  else if (form.discountType === 'fixed' && value != null && value > 0 && minOrder != null && minOrder > 0 && value > minOrder) {
    e.discountValue = 'Fixed discount cannot be more than the Minimum Order';
  }

  if (form.discountType === 'percentage') {
    const cap = toNumber(form.maxDiscount);
    if (cap != null && (!Number.isFinite(cap) || cap <= 0 || cap > PROMO_COUPON_LIMITS.maxAmount)) e.maxDiscount = 'Must be a positive amount';
  }

  const total = toNumber(form.usageLimit);
  if (total != null && (!Number.isInteger(total) || total < 1 || total > PROMO_COUPON_LIMITS.maxUsage)) {
    e.usageLimit = 'Whole number of at least 1 (blank = unlimited)';
  }
  const perUser = toNumber(form.maxUsesPerUser);
  if (perUser == null) e.maxUsesPerUser = 'Max Uses Per User is required';
  else if (!Number.isInteger(perUser) || perUser < 1 || perUser > PROMO_COUPON_LIMITS.maxUsage) e.maxUsesPerUser = 'Whole number of at least 1';
  else if (total != null && Number.isInteger(total) && perUser > total) e.maxUsesPerUser = `Cannot exceed Total Usage Limit (${total})`;

  if (form.audience === 'new_users') {
    const days = toNumber(form.newUserDays);
    if (days == null || !Number.isInteger(days) || days < 1 || days > PROMO_COUPON_LIMITS.maxNewUserDays) {
      e.newUserDays = `Whole number from 1 to ${PROMO_COUPON_LIMITS.maxNewUserDays}`;
    }
  }

  if (!form.validFrom) e.validFrom = 'Valid From is required';
  if (!form.validTo) e.validTo = 'Valid To is required so the coupon expires';
  else if (form.validFrom && form.validTo < form.validFrom) e.validTo = 'Valid To cannot be earlier than Valid From';
  else if (form.validTo < todayIso() && (wasActive || !editingId)) e.validTo = 'Valid To is in the past — the coupon would already be expired';

  const order = Number(form.sortOrder);
  if (!form.sortOrder.trim()) e.sortOrder = 'Display Order is required';
  else if (!Number.isInteger(order) || order < 1 || order > 999) e.sortOrder = 'Whole number from 1 to 999';
  else if (form.module) {
    const dup = rows.find((r) => r._id !== editingId && r.metadata?.module === form.module && couponOrder(r) === order);
    if (dup) e.sortOrder = `Used by ${dup.code} — next free: ${nextFreeOrder(rows, form.module, editingId)}`;
  }

  for (const k of Object.keys(e) as (keyof CouponForm)[]) if (!e[k]) delete e[k];
  return e;
}

const blockNonNumeric = (allowDecimal: boolean) => (ev: KeyboardEvent<HTMLDivElement>) => {
  if (['-', '+', 'e', 'E'].includes(ev.key) || (!allowDecimal && ev.key === '.')) ev.preventDefault();
};

export default function PromosCouponsPage() {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [moduleFilter, setModuleFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'inactive'>('all');
  const [validityFilter, setValidityFilter] = useState<'all' | ValidityState>('all');

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<CouponRow | null>(null);
  const [form, setForm] = useState<CouponForm>(EMPTY_FORM);
  const [codeCustomised, setCodeCustomised] = useState(false);
  const [touched, setTouched] = useState<Partial<Record<keyof CouponForm, boolean>>>({});
  const [submitted, setSubmitted] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [deleteTarget, setDeleteTarget] = useState<CouponRow | null>(null);
  const [actionError, setActionError] = useState('');
  const [actionInfo, setActionInfo] = useState('');

  const retry = (count: number, err: unknown) => (isNetworkError(err) ? count < 3 : count < 1);
  const retryDelay = (attempt: number) => Math.min(1000 * 2 ** attempt, 8000);

  const { data, isLoading, isError, error, refetch, isFetching } = useQuery({
    queryKey: ['masters', PROMO_COUPON_TYPE, 'promo-coupons-page'],
    queryFn: () => getMasters(PROMO_COUPON_TYPE, { limit: 500, page: 1 }),
    retry,
    retryDelay,
  });
  const rows = useMemo(() => (data?.data ?? []) as CouponRow[], [data]);

  const statsQuery = useQuery({
    queryKey: ['masters', PROMO_COUPON_TYPE, 'stats'],
    queryFn: getPromoCouponStats,
    retry,
    retryDelay,
  });
  const expiredNow = statsQuery.data?.expired ?? 0;
  useEffect(() => {
    if (expiredNow > 0) {
      setActionInfo(`${expiredNow} expired coupon${expiredNow === 1 ? ' was' : 's were'} switched off automatically.`);
      queryClient.invalidateQueries({ queryKey: ['masters', PROMO_COUPON_TYPE, 'promo-coupons-page'] });
    }
  }, [expiredNow, queryClient]);

  const statsByKey = useMemo(() => {
    const map = new Map<string, PromoCouponStat>();
    (statsQuery.data?.stats ?? []).forEach((s) => {
      map.set(`${s.tenantId}|${s.code.toUpperCase()}`, s);
      if (!map.has(s.code.toUpperCase())) map.set(s.code.toUpperCase(), s);
    });
    return map;
  }, [statsQuery.data]);
  const statFor = (row: CouponRow) => statsByKey.get(`${row.tenantId}|${row.code.toUpperCase()}`)
    ?? statsByKey.get(row.code.toUpperCase());

  const duplicateNames = useMemo(() => {
    const counts = new Map<string, number>();
    rows.forEach((r) => counts.set(r.name.trim().toLowerCase(), (counts.get(r.name.trim().toLowerCase()) || 0) + 1));
    return new Set([...counts].filter(([, c]) => c > 1).map(([k]) => k));
  }, [rows]);
  const takenCodes = useMemo(() => new Set(rows.map((r) => r.code.trim().toUpperCase())), [rows]);

  const filteredRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows
      .filter((r) => {
        const m = r.metadata || {};
        if (moduleFilter && m.module !== moduleFilter) return false;
        if (statusFilter === 'active' && !r.isActive) return false;
        if (statusFilter === 'inactive' && r.isActive) return false;
        if (validityFilter !== 'all' && validityState(m) !== validityFilter) return false;
        if (!q) return true;
        return [r.name, r.code, m.description].some((v) => String(v || '').toLowerCase().includes(q));
      })
      .sort((a, b) => String(a.metadata?.module || '').localeCompare(String(b.metadata?.module || ''))
        || couponOrder(a) - couponOrder(b));
  }, [rows, search, moduleFilter, statusFilter, validityFilter]);

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['masters', PROMO_COUPON_TYPE] });

  const toggleMut = useMutation({
    mutationFn: ({ id, isActive }: { id: string; isActive: boolean }) => updateMaster(id, { isActive }),
    onSuccess: () => { setActionError(''); setDeleteTarget(null); invalidate(); },
    onError: (err) => { setDeleteTarget(null); setActionError(getApiErrorMessage(err, 'Could not update coupon status')); },
  });

  const deleteMut = useMutation({
    mutationFn: (id: string) => deleteMaster(id),
    onSuccess: () => { setDeleteTarget(null); setActionError(''); invalidate(); },
    onError: (err) => { setDeleteTarget(null); setActionError(getApiErrorMessage(err, 'Could not delete coupon')); },
  });

  const errors = useMemo(
    () => validateForm(form, rows, editing?._id, editing?.isActive ?? true),
    [form, rows, editing],
  );
  const showError = (key: keyof CouponForm) => (submitted || touched[key] ? errors[key] : undefined);

  const saveMut = useMutation({
    mutationFn: async () => {
      const isPercentage = form.discountType === 'percentage';
      const metadata: Record<string, unknown> = {
        module: form.module,
        discountType: form.discountType,
        discountValue: Number(form.discountValue),
        minOrderAmount: blank(form.minOrderAmount) ? 0 : Number(form.minOrderAmount),
        maxDiscount: isPercentage && !blank(form.maxDiscount) ? Number(form.maxDiscount) : '',
        usageLimit: blank(form.usageLimit) ? '' : Number(form.usageLimit),
        maxUsesPerUser: Number(form.maxUsesPerUser),
        audience: form.audience,
        newUserDays: form.audience === 'new_users' ? Number(form.newUserDays) : '',
        validFrom: form.validFrom,
        validTo: form.validTo,
        description: form.description.trim(),
        sortOrder: Number(form.sortOrder),
      };
      const payload = { name: form.name.trim(), sortOrder: Number(form.sortOrder), metadata };
      if (editing) return updateMaster(editing._id, payload);
      return createMaster(PROMO_COUPON_TYPE, { ...payload, code: form.code.trim(), isActive: true });
    },
    onSuccess: () => { setDialogOpen(false); invalidate(); },
    onError: (err) => setSaveError(
      isNetworkError(err)
        ? 'Network error — check your connection and try Save again.'
        : getApiErrorMessage(err, 'Could not save coupon'),
    ),
  });

  const openCreate = () => {
    setEditing(null);
    setForm({ ...EMPTY_FORM, validFrom: todayIso() });
    setCodeCustomised(false);
    setTouched({});
    setSubmitted(false);
    setSaveError('');
    setDialogOpen(true);
  };
  const openEdit = (row: CouponRow) => {
    setEditing(row);
    setForm(rowToForm(row));
    setCodeCustomised(true);
    setTouched({});
    setSubmitted(false);
    setSaveError('');
    setDialogOpen(true);
  };

  /** Applies a change and keeps the auto code in step until the admin types their own. */
  const patchForm = (patch: Partial<CouponForm>, touch: (keyof CouponForm)[]) => {
    setTouched((t) => ({ ...t, ...Object.fromEntries(touch.map((k) => [k, true])) }));
    setForm((f) => {
      const next = { ...f, ...patch };
      if (!editing && !codeCustomised && next.module) {
        next.code = suggestCouponCode(next.module, next.discountType, next.discountValue, takenCodes);
      }
      return next;
    });
  };
  const setField = <K extends keyof CouponForm>(key: K, value: CouponForm[K]) => patchForm({ [key]: value } as Partial<CouponForm>, [key]);

  const onModuleChange = (moduleKey: string) => patchForm({
    module: moduleKey,
    sortOrder: !editing || form.module !== moduleKey ? String(nextFreeOrder(rows, moduleKey, editing?._id)) : form.sortOrder,
  }, ['module']);
  const onDiscountTypeChange = (discountType: string) => patchForm({
    discountType,
    maxDiscount: discountType === 'percentage' ? form.maxDiscount : '',
  }, ['discountType']);
  const regenerateCode = () => {
    setCodeCustomised(false);
    setTouched((t) => ({ ...t, code: true }));
    setForm((f) => ({ ...f, code: suggestCouponCode(f.module, f.discountType, f.discountValue, takenCodes) }));
  };

  const onSave = () => {
    setSubmitted(true);
    if (Object.keys(errors).length) return;
    setSaveError('');
    saveMut.mutate();
  };

  const onDeleteClick = (row: CouponRow) => {
    const used = statFor(row)?.usedCount ?? 0;
    if (used > 0 && !row.isActive) {
      setActionError(`${row.code} has been redeemed ${used} time(s), so it is kept for order history. It is already off and cannot be used.`);
      return;
    }
    setDeleteTarget(row);
  };

  const discountText = (m: CouponMeta) => {
    if (m.discountType === 'percentage') return `${Number(m.discountValue) || 0}% off`;
    if (m.discountType === 'fixed') return `${formatMoney(m.discountValue)} off`;
    return '—';
  };

  const columns: GridColDef<CouponRow>[] = [
    {
      field: 'code',
      headerName: 'Coupon',
      flex: 1.1,
      minWidth: 210,
      renderCell: (p) => {
        const issues = configIssues(p.row);
        const dup = duplicateNames.has(p.row.name.trim().toLowerCase());
        return (
          <Box sx={{ minWidth: 0, py: 0.5 }}>
            <Stack direction="row" spacing={0.5} alignItems="center">
              <Typography variant="body2" fontWeight={700} fontFamily="monospace" noWrap>{p.row.code}</Typography>
              {issues.length > 0 && (
                <Tooltip title={issues.join(' · ')}>
                  <Chip size="small" color="error" icon={<WarningAmber />} label="Needs fix" sx={{ height: 20 }} />
                </Tooltip>
              )}
              {dup && (
                <Tooltip title="Another coupon has the same name">
                  <Chip size="small" color="warning" label="Duplicate" sx={{ height: 20 }} />
                </Tooltip>
              )}
            </Stack>
            <Typography variant="caption" color="text.secondary" noWrap display="block">{p.row.name}</Typography>
          </Box>
        );
      },
    },
    {
      field: 'module',
      headerName: 'Service',
      width: 130,
      valueGetter: (_v, row) => couponModuleLabel(row.metadata?.module),
    },
    {
      field: 'discount',
      headerName: 'Discount',
      width: 150,
      sortable: false,
      renderCell: (p) => {
        const m = p.row.metadata || {};
        return (
          <Box sx={{ py: 0.5 }}>
            <Typography variant="body2" fontWeight={600}>{discountText(m)}</Typography>
            {m.discountType === 'percentage' && (
              <Typography variant="caption" color="text.secondary" display="block">
                {blank(m.maxDiscount) ? 'No max cap' : `Max ${formatMoney(m.maxDiscount)}`}
              </Typography>
            )}
          </Box>
        );
      },
    },
    {
      field: 'minOrderAmount',
      headerName: 'Min. Order',
      width: 120,
      valueGetter: (_v, row) => {
        const v = Number(row.metadata?.minOrderAmount) || 0;
        return v > 0 ? formatMoney(v) : 'No minimum';
      },
    },
    {
      field: 'validity',
      headerName: 'Validity',
      width: 200,
      sortable: false,
      renderCell: (p) => {
        const m = p.row.metadata || {};
        const chip = VALIDITY_CHIP[validityState(m)];
        return (
          <Box sx={{ py: 0.5 }}>
            <Chip size="small" color={chip.color} label={chip.label} sx={{ height: 20 }} />
            <Typography variant="caption" color="text.secondary" display="block">
              {m.validFrom ? formatDate(m.validFrom) : 'Now'} – {m.validTo ? formatDate(m.validTo) : 'No end'}
            </Typography>
          </Box>
        );
      },
    },
    {
      field: 'usage',
      headerName: 'Usage',
      width: 150,
      sortable: false,
      renderCell: (p) => {
        const m = p.row.metadata || {};
        const used = statFor(p.row)?.usedCount ?? 0;
        const limit = Number(m.usageLimit) || 0;
        return (
          <Box sx={{ py: 0.5 }}>
            <Typography variant="body2" color={limit && used >= limit ? 'error.main' : undefined}>
              {used} / {limit ? limit : '∞'} used
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block">
              Max {Number(m.maxUsesPerUser) || 1} per customer
            </Typography>
          </Box>
        );
      },
    },
    {
      field: 'audience',
      headerName: 'Eligible Customers',
      width: 190,
      valueGetter: (_v, row) => audienceLabel(row.metadata?.audience, row.metadata?.newUserDays),
    },
    {
      field: 'redemptions',
      headerName: 'Redemptions',
      width: 150,
      sortable: false,
      renderCell: (p) => {
        if (statsQuery.isLoading) return <Typography variant="caption" color="text.secondary">Loading…</Typography>;
        const s = statFor(p.row);
        if (!s) return <Typography variant="caption" color="text.secondary">Not used yet</Typography>;
        return (
          <Box sx={{ py: 0.5 }}>
            <Typography variant="body2" fontWeight={600}>
              {s.redemptions} · {formatMoney(s.totalDiscount)}
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block">
              {s.lastUsedAt ? `Last ${formatDate(s.lastUsedAt)}` : `${s.uniqueCustomers} customers`}
            </Typography>
          </Box>
        );
      },
    },
    {
      field: 'sortOrder',
      headerName: 'Order',
      width: 70,
      valueGetter: (_v, row) => couponOrder(row) || '—',
    },
    {
      field: 'isActive',
      headerName: 'Active',
      width: 90,
      sortable: false,
      renderCell: (p) => {
        const expired = validityState(p.row.metadata) === 'expired';
        const issues = configIssues(p.row);
        const blockReason = !p.row.isActive && expired
          ? 'Valid To has passed — edit the dates to reactivate'
          : !p.row.isActive && issues.length
            ? `Fix before activating: ${issues.join(' · ')}`
            : '';
        return (
          <Tooltip title={blockReason || (p.row.isActive ? 'Customers can apply this code' : 'Turned off — customers cannot apply it')}>
            <span>
              <Switch
                size="small"
                checked={p.row.isActive}
                disabled={!!blockReason || toggleMut.isPending}
                onChange={(e) => toggleMut.mutate({ id: p.row._id, isActive: e.target.checked })}
              />
            </span>
          </Tooltip>
        );
      },
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 100,
      sortable: false,
      renderCell: (p) => (
        <Stack direction="row" spacing={0.5}>
          <Tooltip title="Edit">
            <IconButton size="small" onClick={() => openEdit(p.row)}><EditOutlined fontSize="small" /></IconButton>
          </Tooltip>
          <Tooltip title="Delete">
            <IconButton size="small" color="error" onClick={() => onDeleteClick(p.row)}>
              <DeleteOutline fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
      ),
    },
  ];

  const needsFixCount = rows.filter((r) => configIssues(r).length > 0).length;
  const blockingErrors = Object.entries(errors) as [keyof CouponForm, string][];
  const deleteUsed = deleteTarget ? statFor(deleteTarget)?.usedCount ?? 0 : 0;
  const isPercentage = form.discountType === 'percentage';

  return (
    <PageShell>
      <PageHeader
        title="Promos & Coupons"
        subtitle="Customer app discount codes for Food, Grocery, Transport, Home Services and Porter."
        actions={(
          <Button variant="contained" startIcon={<Add />} onClick={openCreate} disabled={isError && !rows.length}>
            Add Coupon
          </Button>
        )}
      />

      {isError && (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          action={(
            <Button color="inherit" size="small" startIcon={<Refresh />} onClick={() => { refetch(); statsQuery.refetch(); }} disabled={isFetching}>
              {isFetching ? 'Retrying…' : 'Retry'}
            </Button>
          )}
        >
          {isNetworkError(error)
            ? 'Could not reach the server (network error). Check your connection or VPN, then Retry.'
            : `Could not load coupons: ${getApiErrorMessage(error)}`}
        </Alert>
      )}
      {!isError && statsQuery.isError && (
        <Alert severity="warning" sx={{ mb: 2 }} action={<Button color="inherit" size="small" onClick={() => statsQuery.refetch()}>Retry</Button>}>
          Redemption counts could not be loaded. Coupons are listed, but usage numbers may show 0.
        </Alert>
      )}
      {actionInfo && <Alert severity="info" sx={{ mb: 2 }} onClose={() => setActionInfo('')}>{actionInfo}</Alert>}
      {actionError && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setActionError('')}>{actionError}</Alert>}
      {needsFixCount > 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {needsFixCount} coupon{needsFixCount === 1 ? ' has' : 's have'} an invalid setup (marked <strong>Needs fix</strong>).
          Hover the badge to see why, then edit the coupon. They cannot be switched on until fixed.
        </Alert>
      )}

      <DataTableCard title="Coupons" subtitle={`Showing ${filteredRows.length} of ${rows.length}`}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} sx={{ mb: 2 }}>
          <TextField
            size="small"
            label="Search"
            placeholder="Code, name or description"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{ minWidth: 240, flex: 1 }}
          />
          <TextField select size="small" label="Service" value={moduleFilter} onChange={(e) => setModuleFilter(e.target.value)} sx={{ minWidth: 170 }}>
            <MenuItem value="">All</MenuItem>
            {PROMO_COUPON_MODULES.map((m) => <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>)}
          </TextField>
          <TextField select size="small" label="Status" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value as typeof statusFilter)} sx={{ minWidth: 140 }}>
            <MenuItem value="all">All</MenuItem>
            <MenuItem value="active">Active</MenuItem>
            <MenuItem value="inactive">Inactive</MenuItem>
          </TextField>
          <TextField select size="small" label="Validity" value={validityFilter} onChange={(e) => setValidityFilter(e.target.value as typeof validityFilter)} sx={{ minWidth: 160 }}>
            <MenuItem value="all">All</MenuItem>
            <MenuItem value="live">Running</MenuItem>
            <MenuItem value="scheduled">Scheduled</MenuItem>
            <MenuItem value="expired">Expired</MenuItem>
            <MenuItem value="open">No end date</MenuItem>
          </TextField>
        </Stack>
        <EnhancedDataGrid
          rows={filteredRows}
          columns={columns}
          loading={isLoading}
          getRowId={(r) => r._id}
          rowHeight={60}
          emptyTitle={rows.length ? 'No coupons match the filters' : 'No coupons yet'}
          emptySubtitle={rows.length ? 'Clear the search or filters to see all coupons.' : 'Add a coupon to offer a discount code in the customer app.'}
        />
      </DataTableCard>

      <Dialog open={dialogOpen} onClose={() => !saveMut.isPending && setDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle fontWeight={700}>{editing ? `Edit Coupon — ${editing.code}` : 'Add Coupon'}</DialogTitle>
        <DialogContent dividers>
          {saveError && <Alert severity="error" sx={{ mb: 2 }}>{saveError}</Alert>}
          {submitted && blockingErrors.length > 0 && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              <Typography variant="body2" fontWeight={600}>
                {editing ? 'Cannot save yet' : 'Cannot create the coupon yet'} — fix {blockingErrors.length} field{blockingErrors.length === 1 ? '' : 's'}:
              </Typography>
              <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                {blockingErrors.map(([key, msg]) => (
                  <li key={key}><Typography variant="body2"><strong>{FIELD_LABELS[key]}:</strong> {msg}</Typography></li>
                ))}
              </Box>
            </Alert>
          )}
          <Grid container spacing={2}>
            <Grid item xs={12} md={7}>
              <TextField
                fullWidth
                required
                size="small"
                label="Coupon name"
                value={form.name}
                onChange={(e) => setField('name', e.target.value)}
                onBlur={() => setTouched((t) => ({ ...t, name: true }))}
                inputProps={{ maxLength: PROMO_COUPON_LIMITS.name }}
                error={!!showError('name')}
                helperText={showError('name') || `Internal name, must be unique · ${form.name.length}/${PROMO_COUPON_LIMITS.name}`}
              />
            </Grid>
            <Grid item xs={12} md={5}>
              <TextField
                fullWidth
                required
                size="small"
                label="Code"
                value={form.code}
                placeholder="Select Service to generate"
                onChange={(e) => {
                  setCodeCustomised(true);
                  setTouched((t) => ({ ...t, code: true }));
                  setForm((f) => ({ ...f, code: e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '') }));
                }}
                inputProps={{ maxLength: PROMO_COUPON_LIMITS.codeMax, style: { fontFamily: 'monospace' } }}
                InputProps={{
                  readOnly: !!editing,
                  endAdornment: (
                    <InputAdornment position="end">
                      {editing ? (
                        <Chip size="small" label="Locked" />
                      ) : (
                        <Tooltip title="Auto-generate from Service and discount">
                          <span>
                            <IconButton size="small" edge="end" onClick={regenerateCode} disabled={!form.module} aria-label="Auto-generate code">
                              <AutoFixHigh fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      )}
                    </InputAdornment>
                  ),
                }}
                error={!!showError('code')}
                helperText={showError('code') || (editing
                  ? 'Code cannot change after creation'
                  : codeCustomised ? 'Custom code — click the wand to auto-generate' : 'Auto-generated · A–Z and 0–9, must be unique')}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                minRows={2}
                size="small"
                label="Description"
                value={form.description}
                onChange={(e) => setField('description', e.target.value)}
                inputProps={{ maxLength: PROMO_COUPON_LIMITS.description }}
                error={!!showError('description')}
                helperText={showError('description') || `Shown to customers · ${form.description.length}/${PROMO_COUPON_LIMITS.description}`}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                select
                fullWidth
                required
                size="small"
                label="Service"
                value={form.module}
                onChange={(e) => onModuleChange(e.target.value)}
                error={!!showError('module')}
                helperText={showError('module') || 'Where customers can apply this code'}
              >
                {PROMO_COUPON_MODULES.map((m) => <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                select
                fullWidth
                required
                size="small"
                label="Discount Type"
                value={form.discountType}
                onChange={(e) => onDiscountTypeChange(e.target.value)}
                error={!!showError('discountType')}
                helperText={showError('discountType') || 'Percentage or fixed amount'}
              >
                <MenuItem value="percentage">Percentage</MenuItem>
                <MenuItem value="fixed">Fixed amount</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                required
                size="small"
                type="number"
                label="Discount Value"
                value={form.discountValue}
                onChange={(e) => setField('discountValue', e.target.value)}
                onKeyDown={blockNonNumeric(true)}
                disabled={!form.discountType}
                inputProps={isPercentage ? { min: 1, max: 100, step: 1 } : { min: 1, step: 1 }}
                InputProps={!form.discountType ? undefined : isPercentage
                  ? { endAdornment: <InputAdornment position="end">%</InputAdornment> }
                  : { startAdornment: <InputAdornment position="start">{getCurrencySymbol()}</InputAdornment> }}
                error={!!showError('discountValue')}
                helperText={showError('discountValue') || (!form.discountType
                  ? 'Select a Discount Type first'
                  : isPercentage ? 'Between 1 and 100%' : 'Flat amount off the order')}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                size="small"
                type="number"
                label="Max Discount"
                value={isPercentage ? form.maxDiscount : ''}
                onChange={(e) => setField('maxDiscount', e.target.value)}
                onKeyDown={blockNonNumeric(true)}
                disabled={!isPercentage}
                inputProps={{ min: 1, step: 1 }}
                InputProps={{ startAdornment: <InputAdornment position="start">{getCurrencySymbol()}</InputAdornment> }}
                error={!!showError('maxDiscount')}
                helperText={showError('maxDiscount') || (isPercentage ? 'Optional cap on the % discount' : 'Only for Percentage coupons')}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                size="small"
                type="number"
                label="Minimum Order"
                value={form.minOrderAmount}
                onChange={(e) => setField('minOrderAmount', e.target.value)}
                onKeyDown={blockNonNumeric(true)}
                inputProps={{ min: 0, step: 1 }}
                InputProps={{ startAdornment: <InputAdornment position="start">{getCurrencySymbol()}</InputAdornment> }}
                error={!!showError('minOrderAmount')}
                helperText={showError('minOrderAmount') || '0 = no minimum'}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                required
                size="small"
                type="number"
                label="Display Order"
                value={form.sortOrder}
                onChange={(e) => setField('sortOrder', e.target.value)}
                onKeyDown={blockNonNumeric(false)}
                disabled={!form.module}
                inputProps={{ min: 1, max: 999, step: 1 }}
                error={!!showError('sortOrder')}
                helperText={showError('sortOrder') || (form.module ? `Unique within ${couponModuleLabel(form.module)}` : 'Select a Service first')}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                size="small"
                type="number"
                label="Total Usage Limit"
                value={form.usageLimit}
                onChange={(e) => setField('usageLimit', e.target.value)}
                onKeyDown={blockNonNumeric(false)}
                inputProps={{ min: 1, step: 1 }}
                error={!!showError('usageLimit')}
                helperText={showError('usageLimit') || 'Total redemptions allowed · blank = unlimited'}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                required
                size="small"
                type="number"
                label="Max Uses Per User"
                value={form.maxUsesPerUser}
                onChange={(e) => setField('maxUsesPerUser', e.target.value)}
                onKeyDown={blockNonNumeric(false)}
                inputProps={{ min: 1, step: 1 }}
                error={!!showError('maxUsesPerUser')}
                helperText={showError('maxUsesPerUser') || 'Per customer · cannot exceed the total limit'}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                select
                fullWidth
                size="small"
                label="Eligible Customers"
                value={form.audience}
                onChange={(e) => setField('audience', e.target.value)}
                helperText="Who can apply this code"
              >
                {PROMO_COUPON_AUDIENCES.map((a) => <MenuItem key={a.value} value={a.value}>{a.label}</MenuItem>)}
              </TextField>
            </Grid>
            {form.audience === 'new_users' && (
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  required
                  size="small"
                  type="number"
                  label="New customer window"
                  value={form.newUserDays}
                  onChange={(e) => setField('newUserDays', e.target.value)}
                  onKeyDown={blockNonNumeric(false)}
                  inputProps={{ min: 1, max: PROMO_COUPON_LIMITS.maxNewUserDays, step: 1 }}
                  InputProps={{ endAdornment: <InputAdornment position="end">days</InputAdornment> }}
                  error={!!showError('newUserDays')}
                  helperText={showError('newUserDays') || 'Customers who signed up within this many days'}
                />
              </Grid>
            )}

            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <Grid item xs={12} md={4}>
                <DatePicker
                  label="Valid From *"
                  format={DISPLAY_DATE_FORMAT}
                  value={form.validFrom ? dayjs(form.validFrom) : null}
                  onChange={(v) => setField('validFrom', v && v.isValid() ? v.format('YYYY-MM-DD') : '')}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      size: 'small',
                      error: !!showError('validFrom'),
                      helperText: showError('validFrom') || 'Coupon works from this date',
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <DatePicker
                  label="Valid To *"
                  format={DISPLAY_DATE_FORMAT}
                  value={form.validTo ? dayjs(form.validTo) : null}
                  minDate={dayjs(form.validFrom && form.validFrom > todayIso() ? form.validFrom : todayIso())}
                  onChange={(v) => setField('validTo', v && v.isValid() ? v.format('YYYY-MM-DD') : '')}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      size: 'small',
                      error: !!showError('validTo'),
                      helperText: showError('validTo') || 'Expires at the end of this day',
                    },
                  }}
                />
              </Grid>
            </LocalizationProvider>
          </Grid>
          {!editing && (
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 2 }}>
              New coupons are created Active and switch off automatically after Valid To. Use the Active switch in the list to pause a coupon.
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setDialogOpen(false)} disabled={saveMut.isPending}>Cancel</Button>
          <Button variant="contained" onClick={onSave} disabled={saveMut.isPending}>
            {saveMut.isPending ? 'Saving…' : editing ? 'Save changes' : 'Create coupon'}
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        open={!!deleteTarget}
        title={deleteUsed > 0 ? 'Coupon has been redeemed' : 'Delete coupon?'}
        message={!deleteTarget ? '' : deleteUsed > 0
          ? `${deleteTarget.code} has been redeemed ${deleteUsed} time(s), so it cannot be deleted — order history depends on it.\n\nTurn it off instead so customers can no longer apply it?`
          : `${deleteTarget.isActive ? `${deleteTarget.code} is ACTIVE — customers can apply it at ${couponModuleLabel(deleteTarget.metadata?.module)} checkout right now.\nConsider turning it off instead.\n\n` : ''}Delete ${deleteTarget.code} (${deleteTarget.name})? This cannot be undone.`}
        confirmLabel={deleteUsed > 0 ? 'Turn off' : 'Delete'}
        confirmColor={deleteUsed > 0 ? 'warning' : 'error'}
        verifyText={deleteUsed === 0 && deleteTarget?.isActive ? 'DELETE' : undefined}
        verifyLabel='Type "DELETE" to remove an active coupon'
        loading={deleteMut.isPending || toggleMut.isPending}
        onConfirm={() => {
          if (!deleteTarget) return;
          if (deleteUsed > 0) toggleMut.mutate({ id: deleteTarget._id, isActive: false });
          else deleteMut.mutate(deleteTarget._id);
        }}
        onCancel={() => setDeleteTarget(null)}
      />
    </PageShell>
  );
}
